import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Footer from './Footer';
import WhatsAppButton from './WhatsAppButton';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_vr-techsolutions/artifacts/9vnskgnq_Gemini_Generated_Image_lf1inhlf1inhlf1i.png";

const LegalPage = ({ title, subtitle, intro, sections, lastUpdated }) => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-black flex flex-col" data-testid="legal-page">
      {/* Top Bar */}
      <header className="sticky top-0 z-40 bg-black/85 backdrop-blur-xl border-b border-violet-900/30">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-4 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3 group" data-testid="legal-home-logo">
            <img
              src={LOGO_URL}
              alt="SEBINOR Computación"
              className="h-12 w-auto object-contain group-hover:scale-105 transition-transform"
              style={{ filter: 'drop-shadow(0 0 10px rgba(139, 92, 246, 0.4))' }}
            />
            <span
              className="text-amber-400 text-base hidden sm:inline"
              style={{ fontFamily: "'Dancing Script', cursive" }}
            >
              25 años con vos
            </span>
          </Link>
          <Link
            to="/"
            data-testid="legal-back-home"
            className="inline-flex items-center gap-2 text-sm font-semibold text-white bg-gradient-to-br from-violet-600 to-violet-800 hover:from-violet-500 hover:to-violet-700 rounded-full px-5 py-2.5 shadow-lg shadow-violet-500/20 hover:shadow-violet-500/40 transition-all duration-300"
          >
            <ArrowLeft className="w-4 h-4" />
            Volver al inicio
          </Link>
        </div>
      </header>

      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-violet-600/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-violet-800/10 rounded-full blur-3xl" />
      </div>

      {/* Content */}
      <main className="relative z-10 flex-1">
        <div className="max-w-4xl mx-auto px-4 lg:px-8 py-12 lg:py-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <span className="inline-block px-4 py-1.5 rounded-full text-xs font-mono tracking-widest uppercase bg-violet-600/20 text-violet-300 border border-violet-500/30 mb-5">
              Documento Legal
            </span>
            <h1 className="text-white text-4xl sm:text-5xl lg:text-6xl font-bold mb-4" data-testid="legal-title">
              {title}
            </h1>
            {subtitle && (
              <p className="text-gray-400 text-base sm:text-lg max-w-2xl mx-auto">
                {subtitle}
              </p>
            )}
            {lastUpdated && (
              <p className="text-gray-500 text-sm mt-4 font-mono">
                Última actualización: {lastUpdated}
              </p>
            )}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white/5 backdrop-blur-sm border border-violet-900/30 rounded-2xl p-6 sm:p-10 lg:p-12 space-y-10"
          >
            {intro && (
              <div className="text-gray-300 leading-relaxed space-y-3 pb-8 border-b border-violet-900/30" data-testid="legal-intro">
                {(Array.isArray(intro) ? intro : [intro]).map((p, i) => (
                  <p key={i} className="text-base sm:text-lg">{p}</p>
                ))}
              </div>
            )}
            {sections.map((section, idx) => (
              <section key={idx} data-testid={`legal-section-${idx}`}>
                <h2 className="text-white text-xl sm:text-2xl font-semibold mb-4 flex items-baseline gap-3">
                  <span className="text-violet-400 font-mono text-base">
                    {String(idx + 1).padStart(2, '0')}.
                  </span>
                  {section.heading}
                </h2>
                <div className="text-gray-300 leading-relaxed space-y-3 pl-8 border-l-2 border-violet-900/30">
                  {section.paragraphs.map((p, pIdx) => (
                    <p key={pIdx}>{p}</p>
                  ))}
                  {section.bullets && (
                    <ul className="list-disc list-outside pl-5 space-y-2 text-gray-400">
                      {section.bullets.map((b, bIdx) => (
                        <li key={bIdx}>{b}</li>
                      ))}
                    </ul>
                  )}
                </div>
              </section>
            ))}
          </motion.div>
        </div>
      </main>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default LegalPage;
